import React, { useState, useCallback } from 'react'
import { Button, Card, CardHeader } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import PropTypes from 'prop-types'
import ProductAttributeColoursApiV2 from 'api/v2/admins/product-attribute-colours-api-v2'
import GenericModal from 'components/organisms/generic-modal'
import ProductColourForm from 'domains/product-colour/organisms/form'
import ProductAttributesApiV2 from 'api/v2/admins/product-attributes-api-v2'
import { ALERT_TYPES } from 'constants/alert-constants'
import ProductColourTable from 'domains/product-colour/organisms/table'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const ProductColourListPage = ({ pageUtils }) => {
  const [ colours, setColours ]                     = useState([])
  const [ selectedColour, setSelectedColour ]       = useState({})
  const [ isLoading, setIsLoading ]                 = useState(false)
  const [ isCreateModalOpen, setIsCreateModalOpen ] = useState(false)
  const [ isUpdateModalOpen, setIsUpdateModalOpen ] = useState(false)
  const [ tableState, setTableState ]               = useState({})
  const [ pagination, setPagination ]               = useState({})

  const handleFetchColours = useCallback(async (tableState) => {
    setIsLoading(true)
    await ProductAttributeColoursApiV2.get({ tableState })
      .then((response) => {
        setColours(response.data.product_colours)
        setPagination(response.data.meta)
        setTableState(tableState)
      })
      .catch(() => {
        pageUtils.setAlertMsg('Unable to fetch colours', ALERT_TYPES.DANGER)
      })
      .finally(() => setIsLoading(false))
  }, [ pageUtils ])

  // func to create new product colour
  const handleCreateColour = async (values, formikBag) => {
    formikBag.setSubmitting(true)
    await ProductAttributeColoursApiV2.create({
      label                     : values.label,
      hex_code                  : values.hex_code.slice(1), //remove hashtag in first character of string
      product_colour_filter_ids : values.product_colour_filter_ids
    })
      .then(() => {
        pageUtils.setAlertMsg('Product colour has been created', ALERT_TYPES.SUCCESS)
        toggleCreateModal()
        handleFetchColours(tableState)
        formikBag.resetForm()
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }

  // func to update product colour
  const handleUpdateColour = async (values, formikBag) => {
    formikBag.setSubmitting(true)
    await ProductAttributeColoursApiV2.update({
      id                        : selectedColour.id,
      label                     : values.label,
      hex_code                  : values.hex_code.slice(1), //remove hashtag in first character of string
      product_colour_filter_ids : values.product_colour_filter_ids
    })
      .then(() => {
        pageUtils.setAlertMsg(`Product colour #${selectedColour.id} has been updated`, ALERT_TYPES.SUCCESS)
        toggleUpdateModal()
        setSelectedColour({})
        handleFetchColours(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }

  // func to change product colour status to `enabled`
  const handleEnableColour = useCallback((datum) => {
    ProductAttributesApiV2.enableOption('colour', datum.id)
      .then(() => {
        pageUtils.setAlertMsg(`Product colour #${datum.id} has been enabled`, ALERT_TYPES.SUCCESS)
        handleFetchColours(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }, [ pageUtils, handleFetchColours, tableState ])

  // func to change product colour status to `disabled`
  const handleDisableColour = useCallback((datum) => {
    ProductAttributesApiV2.disableOption('colour', datum.id)
      .then(() => {
        pageUtils.setAlertMsg(`Product colour #${datum.label} has been disabled`, ALERT_TYPES.SUCCESS)
        handleFetchColours(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }, [ pageUtils, handleFetchColours, tableState ])

  // func to open update modal form
  const handleOpenUpdateModal = useCallback((datum) => {
    setSelectedColour(datum)
    setIsUpdateModalOpen(true)
  }, [ setIsUpdateModalOpen, setSelectedColour ])

  const toggleCreateModal = () => setIsCreateModalOpen(!isCreateModalOpen)
  const toggleUpdateModal = () => setIsUpdateModalOpen(!isUpdateModalOpen)

  return (
    <>
      <TitlePage mainTitle='Product Colours List'/>
      <Card>
        <CardHeader>
          <Button
            className='float-right'
            color='primary'
            onClick={ toggleCreateModal }
          >
            Add New Colour
          </Button>
        </CardHeader>

        <ProductColourTable
          data={ colours }
          isLoading={ isLoading }
          pagination={ pagination }
          onFetchData={ handleFetchColours }
          rowButtonProps={{
            handleOpenUpdateModal : handleOpenUpdateModal,
            handleDisableColour   : handleDisableColour,
            handleEnableColour    : handleEnableColour
          }}
        />

      </Card>
      { /* Create Colour Modal Form */ }
      <GenericModal
        isOpen={ isCreateModalOpen }
        toggle={ toggleCreateModal }
        title={ 'Create new Colour' }
        size='md'
      >
        <ProductColourForm
          handleSubmit={ handleCreateColour }
          productColour={{}}
        />
      </GenericModal>
      { /* product colour update Modal Form */ }
      <GenericModal
        isOpen={ isUpdateModalOpen }
        toggle={ toggleUpdateModal }
        title={ 'Update Colour' }
        size='md'
      >
        <ProductColourForm
          handleSubmit={ handleUpdateColour }
          productColour={ selectedColour }
        />
      </GenericModal>
    </>
  )
}
ProductColourListPage.propTypes    = propTypes
ProductColourListPage.defaultProps = defaultProps

export default ProductColourListPage